import { Tree, TreeProps } from "antd";
import classNames from "classnames";
import { throttle } from "lodash";
import type RcTree from "rc-tree";
import type { EventDataNode } from "rc-tree/lib/interface";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DisplayTreeCheckedInfo } from "./DisplayTreeCheckedInfo";
import { DisplayTreeDivider } from "./DisplayTreeDivider";
import styles from "./DisplayTreeStyles.module.css";
import { SelectAllNoneLink } from "./SelectAllNoneLink";
import {
  findNode,
  getParentFolderKeys,
  isStringArray,
} from "./display-tree-utils";
import { useCheckable } from "./hooks/checkable";
import { useExpandable } from "./hooks/expandable";
import { DisplayTreeNode } from "./types";

type DisplayTreeProps = {
  treeData: DisplayTreeNode[];
  selectedKeys?: string[];
  allowMultiSelect?: boolean;
  checkable?: boolean;
  editable?: boolean;
  onCheckNodes?: (node: string[]) => void;
  onSelectNode?: (selectedNode: DisplayTreeNode) => void;
  onDropNode?: (info: {
    dragNode: DisplayTreeNode;
    dropToGap: boolean;
    node: DisplayTreeNode;
    dropPosition: number;
  }) => void;
  onLoad?: (treeNode: DisplayTreeNode) => Promise<void>;
  searchHits?: { keys: string[]; parentKeys: string[] };
  disabled?: boolean;
  // When true, the virtual Tree measures its container and fills the available
  // height (for panels that pass a definite height down, e.g. the config
  // columns). Off by default: the Tree uses a fixed height so it renders at a
  // comfortable size in containers that don't establish a height chain, rather
  // than collapsing to a short panel.
  fillHeight?: boolean;
};

// antd's virtual Tree needs an explicit pixel height. We measure the
// available space instead of hard-coding it; this fallback is used only until
// the first measurement, and for containers that aren't height-bounded (e.g.
// some modals) where the wrapper collapses to its content.
const DEFAULT_TREE_HEIGHT = 820;

const findNodeIndexInTree = (
  treeData: DisplayTreeNode[],
  targetKey: string,
): number => {
  let index = 0;
  const traverse = (nodes: DisplayTreeNode[]): number => {
    for (const node of nodes) {
      if (node.key === targetKey) {
        return index;
      }
      index++;
      if (node.children) {
        const result = traverse(node.children);
        if (result !== -1) {
          return result;
        }
      }
    }
    return -1;
  };
  return traverse(treeData);
};

export const DisplayTree = (props: DisplayTreeProps) => {
  const treeRef = useRef<RcTree>(null);
  // Tracks whether the most recent selection change came from a click inside
  // the tree (vs. an external source such as the drawing canvas). Tree clicks
  // must not auto-scroll: the clicked node is already visible, and scrolling
  // breaks Ctrl/Cmd multi-selection by yanking the viewport around.
  const selectionFromTreeClickRef = useRef<boolean>(false);
  // Last selection we acted on, by value. The parent recreates the
  // selectedKeys array on every render, so we compare contents (not identity)
  // to ignore re-renders that don't actually change the selection.
  const prevSelectionSignatureRef = useRef<string>("");
  // The virtual Tree is sized to its wrapper rather than a fixed height, so it
  // fills the panel instead of leaving white space below a 600px viewport.
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const [treeHeight, setTreeHeight] = useState<number>(DEFAULT_TREE_HEIGHT);
  const {
    treeData,
    selectedKeys,
    allowMultiSelect,
    editable = false,
    checkable = false,
    onCheckNodes,
    onSelectNode,
    onDropNode,
    onLoad,
    searchHits,
    disabled = false,
    fillHeight = false,
  } = props;

  const {
    keys,
    setCheckedKeys,
    setCheckedChildKeys,
    checkExpanded,
    uncheckAll,
    checkExpandedSearchHits,
  } = useCheckable(treeData, onCheckNodes);
  const {
    keys: expandedKeys,
    autoExpandParent,
    onExpandNode: onExpand,
  } = useExpandable();

  useEffect(() => {
    if (searchHits !== undefined) {
      onExpand(searchHits.parentKeys, false);
    } else if (selectedKeys === undefined) {
      onExpand([], false);
    }
  }, [onExpand, selectedKeys, searchHits]);

  useEffect(() => {
    if (searchHits === undefined && selectedKeys !== undefined) {
      const keysToExpand = getParentFolderKeys(selectedKeys, treeData);
      onExpand(keysToExpand, true);
    }
  }, [onExpand, treeData, selectedKeys, searchHits]);

  const onExpandCallback = useCallback(
    (e: (string | number)[]) => {
      // antd supports keys both as numbers & strings
      // our typing only supports strings...
      const keysToExpand: string[] = isStringArray(e) ? e : [];
      if (selectedKeys) {
        const parentKeys = getParentFolderKeys(selectedKeys, treeData);
        keysToExpand.push(...parentKeys);
      }
      onExpand(keysToExpand, false);
    },
    [onExpand, selectedKeys, treeData],
  );

  const searchHitsMap = useMemo(() => {
    if (!searchHits?.keys?.length) {
      return null;
    }
    return new Set(searchHits.keys);
  }, [searchHits]);

  const modifyNodeTitle = useCallback(
    (node: DisplayTreeNode): DisplayTreeNode => {
      const isSearchHit = searchHitsMap?.has(node.key);

      const title = isSearchHit ? (
        <div className={styles["tree-search-value-hit"]}>{node.title}</div>
      ) : (
        node.title
      );

      return {
        ...node,
        title,
        children: node.children
          ? node.children.map(modifyNodeTitle)
          : undefined,
      };
    },
    [searchHitsMap],
  );

  const treeDataMemo = useMemo(() => {
    if (!treeData || treeData.length === 0) {
      return [];
    }

    if (!searchHitsMap) {
      return treeData;
    }

    return treeData.map(modifyNodeTitle);
  }, [treeData, searchHitsMap, modifyNodeTitle]);

  const handleOnSelect = useCallback(
    (
      _selectedKeys: string[],
      info: {
        nativeEvent: MouseEvent;
        selectedNodes: DisplayTreeNode[];
        node?: DisplayTreeNode;
      },
    ) => {
      // Mark this selection change as tree-originated so the auto-scroll
      // effect skips it (the clicked node is already visible).
      selectionFromTreeClickRef.current = true;
      if (info.node) {
        onSelectNode?.(info.node);
      }
      if (info.node && info.nativeEvent.shiftKey && checkable) {
        setCheckedChildKeys(info.node.key, expandedKeys);
      }
    },
    [onSelectNode, checkable, setCheckedChildKeys, expandedKeys],
  );

  const handleOnSelectExpanded = useCallback(
    () => checkExpanded(expandedKeys),
    [checkExpanded, expandedKeys],
  );

  const handleOnSelectSearchHits = useMemo(
    () =>
      searchHits?.keys.length
        ? () => checkExpandedSearchHits(searchHits.keys, expandedKeys)
        : undefined,
    [searchHits, checkExpandedSearchHits, expandedKeys],
  );

  const handleOnCheck = useCallback(
    (
      checkedKeys: { checked: string[] },
      info: { node: EventDataNode<DisplayTreeNode>; nativeEvent: MouseEvent },
    ) => setCheckedKeys(checkedKeys, expandedKeys, info),
    [setCheckedKeys, expandedKeys],
  );

  const expandSelectedNodes = useCallback(() => {
    const parentKeys: string[] = [];
    keys.checked.forEach((key) => {
      const node = findNode(treeData, key);
      node?.ancestorKeys && parentKeys.push(...node.ancestorKeys);
    });
    onExpand(parentKeys, false);
  }, [keys.checked, treeData, onExpand]);

  const scrollTo = useCallback((target: HTMLElement) => {
    const treeHolder = document.getElementsByClassName(
      "ant-tree-list-holder",
    )?.[0] as HTMLElement | undefined;

    if (!treeHolder) {
      return;
    }

    // Batch layout reads in one frame to avoid layout thrash during drag
    requestAnimationFrame(() => {
      const { bottom: currentBottom, top: currentTop } =
        target.getBoundingClientRect();
      const {
        bottom: boxBottom,
        top: boxTop,
        height: containerHeight,
      } = treeHolder.getBoundingClientRect();

      const scrollThresholdDown = Math.round(containerHeight * 0.24);
      const scrollThresholdUp = Math.round(containerHeight * 0.15);
      const scrollAmountDown = Math.round(containerHeight * 0.15);
      const scrollAmountUp = Math.round(containerHeight * 0.1);

      if (currentTop > boxBottom - scrollThresholdDown) {
        treeHolder.scrollTop = treeHolder.scrollTop + scrollAmountDown;
      }
      if (boxTop + scrollThresholdUp > currentBottom) {
        treeHolder.scrollTop = treeHolder.scrollTop - scrollAmountUp;
      }
    });
  }, []);

  const throttledScrollTo = useMemo(() => throttle(scrollTo, 150), [scrollTo]);

  // Scroll to the last selected node, but only when the selection genuinely
  // changes and the change came from outside the tree (e.g. the drawing
  // canvas). Selecting/Ctrl-selecting within the tree, or unrelated parent
  // re-renders (the parent hands us a new selectedKeys array with identical
  // contents on every render), must not move the viewport — and, critically,
  // must not cancel a scroll that is already pending for a real change. The
  // timeout is tracked in a ref (rather than cleared by the effect cleanup)
  // so that no-op re-renders leave a pending scroll intact.
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    // Read and immediately clear the flag. It is consumed on *every* run of
    // this effect, and selectedKeys changes on every render (the parent hands
    // us a fresh array), so the flag can never linger across an unrelated
    // selection change: a tree click and the render it triggers are the same
    // cycle that clears it.
    const fromTreeClick = selectionFromTreeClickRef.current;
    selectionFromTreeClickRef.current = false;

    const selectionSignature = JSON.stringify(selectedKeys ?? []);
    const selectionChanged =
      selectionSignature !== prevSelectionSignatureRef.current;
    prevSelectionSignatureRef.current = selectionSignature;

    // Just a re-render with the same selection: leave any pending scroll alone.
    if (!selectionChanged) {
      return;
    }

    // A genuine selection change supersedes any previously scheduled scroll.
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }

    // Selections made by clicking inside the tree are already visible, so
    // there is nothing to scroll to.
    if (fromTreeClick || !selectedKeys || selectedKeys.length === 0) {
      return;
    }

    const keyToScroll = selectedKeys[selectedKeys.length - 1];
    const selectedNode = findNode(treeData, keyToScroll);
    if (!selectedNode) {
      return;
    }

    // Defer so the node's parent folders (expanded by the auto-expand effect)
    // are rendered before we ask the virtual list to scroll to the node.
    scrollTimeoutRef.current = setTimeout(() => {
      scrollTimeoutRef.current = null;
      try {
        // Prefer Tree's built-in scrollTo (handles the virtual list correctly)
        if (treeRef.current && typeof treeRef.current.scrollTo === "function") {
          treeRef.current.scrollTo({ key: selectedNode.key });
          return;
        }

        // Single query by data-key first (virtual list only renders visible nodes)
        const targetNode = document.querySelector(
          `[data-key="${selectedNode.key}"]`,
        ) as HTMLElement;
        if (targetNode) {
          targetNode.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
          return;
        }

        // Virtualized tree: scroll container by estimated index (no querySelectorAll)
        const treeHolder = document.querySelector(
          ".ant-tree-list-holder",
        ) as HTMLElement;
        if (treeHolder) {
          const nodeIndex = findNodeIndexInTree(treeData, selectedNode.key);
          if (nodeIndex !== -1) {
            const estimatedScrollTop = nodeIndex * 50;
            treeHolder.scrollTop = estimatedScrollTop;
          }
        }
      } catch (error) {
        console.error("Auto-scroll: Failed to scroll to selected node:", error);
      }
    }, 300);
  }, [selectedKeys, treeData]);

  useEffect(() => {
    // Only measure when the caller opts into filling. Otherwise the Tree keeps
    // its fixed DEFAULT_TREE_HEIGHT, which avoids shrinking to fit short panels
    // (or collapsing where the container passes no definite height down).
    if (!fillHeight) {
      return;
    }
    const el = treeContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      const measured = Math.round(entries[0]?.contentRect.height ?? 0);
      // Ignore zero/sub-pixel jitter; only adopt a real, positive height.
      if (measured > 0) {
        setTreeHeight((prev: number) => (prev === measured ? prev : measured));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fillHeight]);

  const handleDragOver = useCallback(
    (info: any) => {
      if (info.event.target) {
        throttledScrollTo(info.event.target);
      }
    },
    [throttledScrollTo],
  );

  return (
    <div className={styles["display-tree-root"]}>
      {checkable && (
        <div className={classNames(styles["select-all-none-container"])}>
          {keys.checked.length > 0 && (
            <>
              <DisplayTreeCheckedInfo
                numberCheckedKeys={keys.checked.length}
                expandCheckedCallback={expandSelectedNodes}
              />
              <DisplayTreeDivider />
            </>
          )}
          <SelectAllNoneLink
            selectExpandedCallback={handleOnSelectExpanded}
            selectNoneCallback={uncheckAll}
            selectSearchHitsCallback={handleOnSelectSearchHits}
          />
        </div>
      )}
      <div ref={treeContainerRef} className={styles["tree-fill"]}>
        <Tree
          ref={treeRef}
          showLine={{ showLeafIcon: false }}
          treeData={treeDataMemo}
          draggable={editable}
          blockNode
          multiple={allowMultiSelect}
          // We need to optionally pass the selectedKeys as any other
          // option (undefined, null, empty array) makes the component
          // controlled and then there is no way to modify the selection
          {...(selectedKeys ? { selectedKeys: selectedKeys } : {})}
          /* TODO: fix casting
        Antd's tree component expects a specific type. If we give it more properties than it expects (which we do here) it will not be able todo 
        anything with that data as it does not know about it.
        Any use of these extra parameters we have in our logic/callbacks we are leaving on here for our own convenience, not antd's.
        The callbacks should use antd's expected types and additional lofic added to retireve all of the DisplayTreeNode properties.
        */
          onDrop={onDropNode as TreeProps["onDrop"]}
          onSelect={handleOnSelect as unknown as TreeProps["onSelect"]} // unknown required as intermediory as antd onSelect can't resolve DisplayTreeNode[] to a DataNode[] on selectedNodes
          onCheck={handleOnCheck as unknown as TreeProps["onCheck"]}
          onExpand={onExpandCallback as TreeProps["onExpand"]}
          loadData={onLoad as TreeProps["loadData"]}
          checkable={checkable}
          checkedKeys={keys.checked}
          checkStrictly
          expandedKeys={expandedKeys}
          autoExpandParent={autoExpandParent}
          disabled={disabled}
          height={treeHeight}
          onDragOver={handleDragOver}
          virtual
        />
      </div>
    </div>
  );
};
